import { Injectable } from "@angular/core"
import { BehaviorSubject, Observable, first, tap, catchError, of, switchMap, filter, take } from "rxjs"
import {
    CategoryService,
    LocationsService,
    StaticFilesService,
    VdkCardService,
    KngService,
    AverageMonthlyIncomeService,
    DependenciesService,
} from "."
import {
    Category,
    IContext,
    createContext,
    CreateCategory,
    setCurrentEditedMasterId,
    ModifyCategoryFactory,
    IResult,
    ResultFactory,
    ModifyObject,
    ModifyObjectFactory,
    Node,
    Location,
    ModifyLocationFactory,
    CreateLocation,
    EmployeeType,
    CostCenter,
    Positions,
    PdfFileResult,
    VdkMbrads,
    Application,
    ModifyApplicationFactory,
    ModifyApplication,
    CreateSuperiorMap,
    AverageMonthlyIncome,
    ModifyAverageMonthlyIncomeFactory,
    CreateAverageMonthlyIncome,
    PositionScore,
    Position,
    CreatePositionScore,
    SuperiorMap,
    ContractGenerate,
    FileContent,
} from "../models"
import { JsonPatchDocumentGenerator } from "../helpers/JsonPatchDocumentGenerator"

@Injectable({
    providedIn: "root",
})
export class ContextService {
    private _context: IContext
    private _jsonPatchDocumentGenerator: JsonPatchDocumentGenerator = new JsonPatchDocumentGenerator()
    private _categoriesRequested: boolean = false
    private _categoriesLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
    private _formTypes: string[] = ["add", "edit"]
    private _ignoreFields: string[] = [
        "_id",
        "id",
        "masterId",
        "createdByUser",
        "modifiedByUser",
        "createdDate",
        "modifiedDate",
        "version",
        "level",
    ]

    constructor(
        private _categoryService: CategoryService,
        private _dependenciesService: DependenciesService,
        private _locationsService: LocationsService,
        private _staticFilesService: StaticFilesService,
        private _vdkCardService: VdkCardService,
        private _kngService: KngService,
        private _averageMonthlyIncomeService: AverageMonthlyIncomeService,
    ) {
        this._context = createContext()

        this._kngService.Get().subscribe(kng => {
            this.setKng(kng)
        })

        // this._locationsService.Get().subscribe(locations => {
        //     this.setLocations(locations)
        // })
    }

    InitKng(): void {
        if (this._context.kng().subjects.items.value.length > 0) return
        this._kngService.Get().subscribe(kng => {
            this.setKng(kng)
        })
    }

    InitKngAvgMonthlyIncome(): void {
        if (this._context.avgMonthlyIncome().subjects.items.value.length > 0) return
        this._averageMonthlyIncomeService.GetAllAverageMonthlyIncome().subscribe(avgMonthlyIncome => {
            this.setAvgMonthlyIncome(avgMonthlyIncome)
        })
    }

    InitKngPositionScores(): void {
        this._kngService.GetPositionScores().subscribe(posScores => {
            this._context.kngPositionScores().positionScores.next(posScores.positionScores)
            this._context.kngPositionScores().unassignedPositions.next(posScores.unassignedPositions)
        })
    }

    InitCategories(): void {
        if (this._categoriesRequested) return
        this._categoriesRequested = true
        this._categoryService.GetAllCached().subscribe({
            next: (categories: Category[]) => {
                this.setCategories(categories)
                this._categoriesLoaded.next(true)
            },
            error: () => {
                this._categoriesRequested = false
                this._categoriesLoaded.next(true)
            },
        })
    }

    categoriesLoaded$(): Observable<boolean> {
        this.InitCategories()
        return this._categoriesLoaded.pipe(filter(Boolean), take(1))
    }

    InitLocations(): void {
        if (this._context.locations().subjects.items.value.length > 0) return
        this._locationsService.GetAllCached().subscribe(locations => {
            this.setLocations(locations)
        })
    }

    InitDependencies(): void {
        this._dependenciesService.InitDependencies()
    }

    OrgUnits(): Observable<Node | undefined> {
        return this._dependenciesService.OrgUnitTree().asObservable()
    }

    RawOrgUnits(): Node | undefined {
        return this._dependenciesService.OrgUnitTree().value
    }

    Positions(): Observable<Positions | undefined> {
        return this._dependenciesService.Positions().asObservable()
    }

    OrgUnitTree(): Node | undefined {
        return this._dependenciesService.OrgUnitTree().value
    }

    RawPositions(): Positions | undefined {
        return this._dependenciesService.Positions().value
    }

    CostCenters(): CostCenter[] {
        return this._dependenciesService.CostCenters().value
    }

    CostCentersObservable(): Observable<CostCenter[]> {
        return this._dependenciesService.CostCenters().asObservable()
    }

    EmployeeContractsObservable(): Observable<EmployeeType[]> {
        return this._dependenciesService.EmployeeContracts().asObservable()
    }

    EmployeeContracts(): EmployeeType[] {
        return this._dependenciesService.EmployeeContracts().value
    }

    setAddItem(contextKey: string, key: string, value: any) {
        if (this._context.get(contextKey) !== undefined) {
            this._context.get(contextKey)!.setFieldAdd(key, value)
        }
    }

    setEditItem(contextKey: string, key: string, value: any) {
        if (this._context.get(contextKey) !== undefined) {
            this._context.get(contextKey)!.setFieldEdit(key, value)
        }
    }

    setItem(contextKey: string, key: string, value: any, formType: string) {
        if (formType === this._formTypes[0]) {
            this.setAddItem(contextKey, key, value)
        } else if (formType === this._formTypes[1]) {
            this.setEditItem(contextKey, key, value)
        }
    }

    getAddKey(): string {
        return this._formTypes[0]
    }

    getEditKey(): string {
        return this._formTypes[1]
    }

    getCategoriesSubject(): BehaviorSubject<any[]> | undefined {
        return this._context.categories().subjects.items
    }

    getLocationsSubject(): BehaviorSubject<any[]> | undefined {
        return this._context.locations().subjects.items
    }

    getKngSubject(): BehaviorSubject<Application[]> | undefined {
        return this._context.kng().subjects.items
    }

    getAvgMonthlyIncomeSubject(): BehaviorSubject<any[]> | undefined {
        return this._context.avgMonthlyIncome().subjects.items
    }

    getAddCategorySubject(): BehaviorSubject<any> | undefined {
        return this._context.categories().subjects.currentAddItem
    }

    getAddLocationSubject(): BehaviorSubject<any> | undefined {
        return this._context.locations().subjects.currentAddItem
    }

    getAddKngSubject(): BehaviorSubject<any> | undefined {
        return this._context.kng().subjects.currentAddItem
    }

    getAddAvgMonthlyIncomeSubject(): BehaviorSubject<any> | undefined {
        return this._context.avgMonthlyIncome().subjects.currentAddItem
    }

    getEditCategorySubject(): BehaviorSubject<any> | undefined {
        return this._context.categories().subjects.currentEditItem
    }

    getEditLocationSubject(): BehaviorSubject<any> | undefined {
        return this._context.locations().subjects.currentEditItem
    }

    getEditKngSubject(): BehaviorSubject<any> | undefined {
        return this._context.kng().subjects.currentEditItem
    }

    getEditAvgMonthlyIncomeSubject(): BehaviorSubject<any> | undefined {
        return this._context.avgMonthlyIncome().subjects.currentEditItem
    }

    getCategoriesResultSubject(): BehaviorSubject<IResult | null> | undefined {
        return this._context.categories().subjects.result
    }

    getLocationsResultSubject(): BehaviorSubject<IResult | null> | undefined {
        return this._context.locations().subjects.result
    }

    getKngResultSubject(): BehaviorSubject<IResult | null> | undefined {
        return this._context.kng().subjects.result
    }

    getKngSubmittedResultSubject(): BehaviorSubject<IResult | null> | undefined {
        return this._context.kngSubmittedApplications().result
    }

    getAvgMonthlyIncomeResultSubject(): BehaviorSubject<IResult | null> | undefined {
        return this._context.avgMonthlyIncome().subjects.result
    }

    setCategories(value: any[]) {
        this._context.categories().subjects.items.next(value)
    }

    setLocations(value: Location[]) {
        this._context.locations().subjects.items.next(value)
    }

    setKng(value: Application[]) {
        this._context.kng().subjects.items.next(value)
    }

    setAvgMonthlyIncome(value: AverageMonthlyIncome[]) {
        this._context.avgMonthlyIncome().subjects.items.next(value)
    }

    resetCategories() {
        this._categoryService
            .Get()
            .pipe(first())
            .subscribe((categories: Category[]) => {
                this.setCategories(categories)
            })
    }

    resetLocations() {
        this._locationsService
            .Get()
            .pipe(first())
            .subscribe(locations => {
                this.setLocations(locations)
            })
    }

    resetKng() {
        this._kngService
            .Get()
            .pipe(first())
            .subscribe(kng => {
                this.setKng(kng)
            })
    }

    resetAvgMonthlyIncome() {
        this._averageMonthlyIncomeService
            .GetAllAverageMonthlyIncome()
            .pipe(first())
            .subscribe(avgMonthlyIncome => {
                this.setAvgMonthlyIncome(avgMonthlyIncome)
            })
    }

    getCategoriesKey(): string {
        return this._context.categories().getKey()
    }

    getLocationsKey(): string {
        return this._context.locations().getKey()
    }

    getKngKey(): string {
        return this._context.kng().getKey()
    }

    getAvgMonthlyIncomeKey(): string {
        return this._context.avgMonthlyIncome().getKey()
    }

    saveCategory() {
        if (this._context.categories() !== undefined) {
            const category = this._context.categories().subjects.currentAddItem.value
            if (category !== undefined) {
                if (category.parentId === "") {
                    category.parentId = null
                }
                this.addCategory(category)
            }
        }
    }

    saveLocation() {
        if (this._context.locations() !== undefined) {
            const location = this._context.locations().subjects.currentAddItem.value
            if (location !== undefined) {
                this.addLocation(location)
            }
        }
    }

    saveAvgMonthlyIncome() {
        if (this._context.avgMonthlyIncome() !== undefined) {
            const avgMonthlyIncome = this._context.avgMonthlyIncome().subjects.currentAddItem.value
            if (avgMonthlyIncome !== undefined) {
                this.addAvgMonthlyIncome(avgMonthlyIncome)
            }
        }
    }

    draftCategory() {
        if (this._context.categories() !== undefined) {
            const category = this._context.categories().subjects.currentAddItem.value
            if (category !== undefined) {
                const draftCategory = { ...category, isDraft: true }
                if (draftCategory.parentId === "") {
                    draftCategory.parentId = null
                }
                this.addCategory(draftCategory)
            }
        }
    }

    draftLocation() {
        if (this._context.locations() !== undefined) {
            const location = this._context.locations().subjects.currentAddItem.value
            if (location !== undefined) {
                const draftLocation = { ...location, isDraft: true }
                this.addLocation(draftLocation)
            }
        }
    }

    modifyCategory() {
        if (this._context.categories() !== undefined) {
            const newCat = JSON.parse(JSON.stringify(this._context.categories().subjects.currentEditItem.value))
            if (newCat !== undefined && this._context.categories().currentEditedMasterId !== undefined) {
                if (newCat.isDeleted === true) {
                    this.deleteCategory(this._context.categories().currentEditedMasterId!)
                } else {
                    if (newCat.parentId === "") {
                        newCat.parentId = null
                    }
                    const oldValue = this._context
                        .categories()
                        .subjects.items.value.find(c => c.masterId === this._context.categories().currentEditedMasterId)
                    const oldCat = ModifyCategoryFactory.from(oldValue)
                    const patchDocument = this._jsonPatchDocumentGenerator.getJsonPatchDocument(
                        oldCat,
                        newCat,
                        this._ignoreFields
                    )
                    const modifyObject = ModifyObjectFactory.create(patchDocument, oldValue!.version)
                    this.editCategory(this._context.categories().currentEditedMasterId!, modifyObject)
                }
            }
        }
    }

    modifyLocation() {
        if (this._context.locations() !== undefined) {
            const newLocation = JSON.parse(JSON.stringify(this._context.locations().subjects.currentEditItem.value))
            if (newLocation !== undefined && this._context.locations().currentEditedMasterId !== undefined) {
                if (newLocation.isDeleted) {
                    this.deleteLocation(this._context.locations().currentEditedMasterId!)
                } else {
                    const oldValue = this._context
                        .locations()
                        .subjects.items.value.find(l => l.masterId === this._context.locations().currentEditedMasterId)
                    const oldLocation = ModifyLocationFactory.from(oldValue)
                    const patchDocument = this._jsonPatchDocumentGenerator.getJsonPatchDocument(
                        oldLocation,
                        newLocation,
                        this._ignoreFields
                    )
                    const modifyObject = ModifyObjectFactory.create(patchDocument, oldValue!.version)
                    this.editLocation(this._context.locations().currentEditedMasterId!, modifyObject)
                }
            }
        }
    }

    modifyKng() {
        if (this._context.kng() !== undefined) {
            const newKng = JSON.parse(JSON.stringify(this._context.kng().subjects.currentEditItem.value))
            if (newKng !== undefined && this._context.kng().currentEditedMasterId !== undefined) {
                const oldValue = this._context
                    .kng()
                    .subjects.items.value.find(l => l.masterId === this._context.kng().currentEditedMasterId)
                const oldKng = ModifyApplicationFactory.from(oldValue)
                const patchDocument = this._jsonPatchDocumentGenerator.getJsonPatchDocument(
                    oldKng,
                    newKng,
                    this._ignoreFields
                )
                const modifyObject = ModifyObjectFactory.create(patchDocument, oldValue!.version)
                this.editKng(this._context.kng().currentEditedMasterId!, modifyObject)
            }
        }
    }

    modifyAvgMonthlyIncome() {
        if (this._context.avgMonthlyIncome() !== undefined) {
            const newAvgMonthlyIncome = JSON.parse(JSON.stringify(this._context.avgMonthlyIncome().subjects.currentEditItem.value))
            if (newAvgMonthlyIncome !== undefined && this._context.avgMonthlyIncome().currentEditedMasterId !== undefined) {
                if (newAvgMonthlyIncome.isDeleted === true) {
                    this.deleteAvgMonthlyIncome(this._context.avgMonthlyIncome().currentEditedMasterId!)
                } else {
                    const oldValue = this._context
                        .avgMonthlyIncome()
                        .subjects.items.value.find(ami => ami.masterId === this._context.avgMonthlyIncome().currentEditedMasterId)
                    const oldAvgMonthlyIncome = ModifyAverageMonthlyIncomeFactory.from(oldValue)
                    const patchDocument = this._jsonPatchDocumentGenerator.getJsonPatchDocument(
                        oldAvgMonthlyIncome,
                        newAvgMonthlyIncome,
                        this._ignoreFields
                    )
                    const modifyObject = ModifyObjectFactory.create(patchDocument, oldValue!.version)
                    this.editAvgMonthlyIncome(this._context.avgMonthlyIncome().currentEditedMasterId!, modifyObject)
                }
            }
        }
    }

    addCategory(category: CreateCategory) {
        this._categoryService
            .Create(category)
            .pipe(first())
            .subscribe({
                next: (category: Category) => {
                    this.setCategories([...this._context.categories().subjects.items.value, category])
                    this._context.categories().resetAddItem()
                    this._context.categories().sendResult(ResultFactory.createSuccess("Uspješno uneseno"))
                },
                error: error => {
                    if (typeof error.error === "string") {
                        this._context.categories().sendResult(ResultFactory.createError(error.error, []))
                    } else if (typeof error.error === "object" && error.error.message) {
                        this._context
                            .categories()
                            .sendResult(
                                ResultFactory.createError(error.error.message, error.error.incorrectFields || [])
                            )
                    } else {
                        this._context.categories().sendResult(ResultFactory.createError("An error occurred", []))
                    }
                },
            })
    }

    addLocation(location: CreateLocation) {
        this._locationsService
            .Create(location)
            .pipe(first())
            .subscribe({
                next: res => {
                    this.setLocations([...this._context.locations().subjects.items.value, res])
                    this._context.locations().resetAddItem()
                    this._context.locations().sendResult(ResultFactory.createSuccess("Uspješno uneseno"))
                },
                error: error => {
                    if (typeof error.error === "string") {
                        this._context.locations().sendResult(ResultFactory.createError(error.error, []))
                    } else if (typeof error.error === "object" && error.error.message) {
                        this._context
                            .locations()
                            .sendResult(
                                ResultFactory.createError(error.error.message, error.error.incorrectFields || [])
                            )
                    } else {
                        this._context.locations().sendResult(ResultFactory.createError("An error occurred", []))
                    }
                },
            })
    }

    addAvgMonthlyIncome(avrgMonthlyIncome: CreateAverageMonthlyIncome) {
        this._averageMonthlyIncomeService
            .CreateAverageMonthlyIncome(avrgMonthlyIncome)
            .pipe(first())
            .subscribe({
                next: res => {
                    this.setAvgMonthlyIncome([...this._context.avgMonthlyIncome().subjects.items.value, res])
                    this._context.avgMonthlyIncome().resetAddItem()
                    this._context.avgMonthlyIncome().sendResult(ResultFactory.createSuccess("Uspješno uneseno"))
                },
                error: error => {
                    if (typeof error.error === "string") {
                        this._context.avgMonthlyIncome().sendResult(ResultFactory.createError(error.error, []))
                    } else if (typeof error.error === "object" && error.error.message) {
                        this._context
                            .avgMonthlyIncome()
                            .sendResult(
                                ResultFactory.createError(error.error.message, error.error.incorrectFields || [])
                            )
                    } else {
                        this._context.avgMonthlyIncome().sendResult(ResultFactory.createError("An error occurred", []))
                    }
                },
            })
    }

    editCategory(masterId: string, modifyObject: ModifyObject) {
        this._categoryService
            .Modify(masterId, modifyObject)
            .pipe(first())
            .subscribe({
                next: (category: Category) => {
                    this.setCategories(
                        this._context
                            .categories()
                            .subjects.items.value.map(c => (c.masterId === masterId ? category : c))
                    )
                    this._context
                        .categories()
                        .sendResult(ResultFactory.createSuccess("Uspješno izmijenjeno", this._formTypes[1]))
                    this.setCurrentEditCategory(undefined)
                },
                error: error => {
                    if (typeof error.error === "string") {
                        this._context
                            .categories()
                            .sendResult(ResultFactory.createError(error.error, [], this._formTypes[1]))
                    } else if (typeof error.error === "object" && error.error.message) {
                        this._context
                            .categories()
                            .sendResult(
                                ResultFactory.createError(
                                    error.error.message,
                                    error.error.incorrectFields || [],
                                    this._formTypes[1]
                                )
                            )
                    } else {
                        this._context
                            .categories()
                            .sendResult(ResultFactory.createError("An error occurred", [], this._formTypes[1]))
                    }
                },
            })
    }

    editLocation(masterId: string, modifyObject: ModifyObject) {
        this._locationsService
            .Modify(masterId, modifyObject)
            .pipe(first())
            .subscribe({
                next: res => {
                    this.setLocations(
                        this._context.locations().subjects.items.value.map(l => (l.masterId === masterId ? res : l))
                    )
                    this._context
                        .locations()
                        .sendResult(ResultFactory.createSuccess("Uspješno izmijenjeno", this._formTypes[1]))
                    this.setCurrentEditLocation(undefined)
                },
                error: error => {
                    if (typeof error.error === "string") {
                        this._context
                            .locations()
                            .sendResult(ResultFactory.createError(error.error, [], this._formTypes[1]))
                    } else if (typeof error.error === "object" && error.error.message) {
                        this._context
                            .locations()
                            .sendResult(
                                ResultFactory.createError(
                                    error.error.message,
                                    error.error.incorrectFields || [],
                                    this._formTypes[1]
                                )
                            )
                    } else {
                        this._context
                            .locations()
                            .sendResult(ResultFactory.createError("An error occurred", [], this._formTypes[1]))
                    }
                },
            })
    }

    editKng(masterId: string, modifyObject: ModifyObject) {
        this._kngService
            .AdminModify(masterId, modifyObject)
            .pipe(first())
            .subscribe({
                next: res => {
                    this.setKng(
                        this._context.kng().subjects.items.value.map(k => (k.masterId === masterId ? res : k))
                    )
                    this._context
                        .kng()
                        .sendResult(ResultFactory.createSuccess("Uspješno izmijenjeno", this._formTypes[1]))
                    this.setCurrentEditKng(undefined)
                },
                error: error => {
                    if (typeof error.error === "string") {
                        this._context
                            .kng()
                            .sendResult(ResultFactory.createError(error.error, [], this._formTypes[1]))
                    } else if (typeof error.error === "object" && error.error.message) {
                        this._context
                            .kng()
                            .sendResult(
                                ResultFactory.createError(
                                    error.error.message,
                                    error.error.incorrectFields || [],
                                    this._formTypes[1]
                                )
                            )
                    } else {
                        this._context
                            .kng()
                            .sendResult(ResultFactory.createError("An error occurred", [], this._formTypes[1]))
                    }
                },
            })
    }

    deleteAvgMonthlyIncome(masterId: string) {
        this._averageMonthlyIncomeService
            .Delete(masterId)
            .pipe(first())
            .subscribe({
                next: () => {
                    this.setAvgMonthlyIncome(
                        this._context.avgMonthlyIncome().subjects.items.value.filter(ami => ami.masterId !== masterId)
                    )
                    this._context.avgMonthlyIncome().resetEditItem(undefined)
                    this._context
                        .avgMonthlyIncome()
                        .sendResult(ResultFactory.createSuccess("Uspješno obrisano", this._formTypes[1]))
                    this.setCurrentEditAvgMonthlyIncome(undefined)
                },
                error: error => {
                    this._context.avgMonthlyIncome().sendResult(ResultFactory.createError(error.error, [], this._formTypes[1]))
                },
            })
    }

    editAvgMonthlyIncome(masterId: string, modifyObject: ModifyObject) {
        this._averageMonthlyIncomeService
            .ModifyAverageMonthlyIncome(masterId, modifyObject)
            .pipe(first())
            .subscribe({
                next: res => {
                    this.setAvgMonthlyIncome(
                        this._context.avgMonthlyIncome().subjects.items.value.map(ami => (ami.masterId === masterId ? res : ami))
                    )
                    this._context
                        .avgMonthlyIncome()
                        .sendResult(ResultFactory.createSuccess("Uspješno izmijenjeno", this._formTypes[1]))
                    this.setCurrentEditAvgMonthlyIncome(undefined)
                },
                error: error => {
                    if (typeof error.error === "string") {
                        this._context
                            .avgMonthlyIncome()
                            .sendResult(ResultFactory.createError(error.error, [], this._formTypes[1]))
                    } else if (typeof error.error === "object" && error.error.message) {
                        this._context
                            .avgMonthlyIncome()
                            .sendResult(
                                ResultFactory.createError(
                                    error.error.message,
                                    error.error.incorrectFields || [],
                                    this._formTypes[1]
                                )
                            )
                    } else {
                        this._context
                            .avgMonthlyIncome()
                            .sendResult(ResultFactory.createError("An error occurred", [], this._formTypes[1]))
                    }
                },
            })
    }

    deleteCategory(masterId: string) {
        this._categoryService
            .Delete(masterId)
            .pipe(first())
            .subscribe({
                next: () => {
                    this.setCategories(
                        this._context.categories().subjects.items.value.filter(c => c.masterId !== masterId)
                    )
                    this._context.categories().resetEditItem(undefined)
                    this._context
                        .categories()
                        .sendResult(ResultFactory.createSuccess("Uspješno obrisano", this._formTypes[1]))
                    this.setCurrentEditCategory(undefined)
                },
                error: error => {
                    this._context.categories().sendResult(ResultFactory.createError(error.error, [], this._formTypes[1]))
                },
            })
    }

    deleteLocation(masterId: string) {
        this._locationsService
            .Delete(masterId)
            .pipe(first())
            .subscribe({
                next: () => {
                    this.setLocations(
                        this._context.locations().subjects.items.value.filter(l => l.masterId !== masterId)
                    )
                    this._context.locations().resetEditItem(undefined)
                    this._context
                        .locations()
                        .sendResult(ResultFactory.createSuccess("Uspješno obrisano", this._formTypes[1]))
                    this.setCurrentEditLocation(undefined)
                },
                error: error => {
                    this._context.locations().sendResult(ResultFactory.createError(error.error, [], this._formTypes[1]))
                },
            })
    }

    setCurrentEditCategory(masterId: string | undefined) {
        setCurrentEditedMasterId(this._context.categories(), masterId)
        this._context.categories().resetEditItem(masterId)
    }

    setCurrentEditLocation(masterId: string | undefined) {
        setCurrentEditedMasterId(this._context.locations(), masterId)
        this._context.locations().resetEditItem(masterId)
    }

    setCurrentEditKng(masterId: string | undefined) {
        setCurrentEditedMasterId(this._context.kng(), masterId)
        this._context.kng().resetEditItem(masterId)
    }

    setCurrentEditAvgMonthlyIncome(masterId: string | undefined) {
        setCurrentEditedMasterId(this._context.avgMonthlyIncome(), masterId)
        this._context.avgMonthlyIncome().resetEditItem(masterId)
    }

    currentEditedCategory(): string | undefined {
        return this._context.categories()?.currentEditedMasterId
    }

    currentEditedLocation(): string | undefined {
        return this._context.locations()?.currentEditedMasterId
    }

    resetAddCategory() {
        this._context.categories().resetAddItem()
    }

    resetAddLocation() {
        this._context.locations().resetAddItem()
    }

    resetAddAvgMonthlyIncome() {
        this._context.avgMonthlyIncome().resetAddItem()
    }

    categoryName(masterid: string) {
        if (masterid != null) {
            for (let item of this._context.categories().subjects.items.value) {
                if (item.masterId === masterid) {
                    return item.names.hr
                }
            }
        } else {
            return masterid
        }
    }

    locationNames(ids: string[]) {
        if (ids.length <= 0) return ids
        return this._context
            .locations()
            .subjects.items.value.filter(l => ids.includes(l.masterId))
            .map(l => l.names.hr)
    }

    categoriesNames(masterids: string[]) {
        let categoriesNames: string[] = []
        if (masterids.length > 0) {
            for (let categoryid of masterids) {
                for (let item of this._context.categories().subjects.items.value) {
                    if (item.masterId === categoryid) {
                        categoriesNames.push(item.names.hr)
                    }
                }
            }
            return categoriesNames
        } else {
            return masterids
        }
    }

    getStaticPdfFile(filename: string): Observable<PdfFileResult> {
        return this._staticFilesService.GetPdfFile(filename).pipe(first())
    }

    getKngFile(fileName: string): Observable<PdfFileResult> {
        return this._kngService.GetFileContent(fileName)
    }

    getSubmittedKngApplications(): Observable<Application[]> {
        return this._kngService.GetSubmitted().pipe(
            tap(res => this.initKngSubmittedApplications(res))
        )
    }

    submittedKngApplications(): Observable<Application[]> {
        return this._context.kngSubmittedApplications().submittedApplications
    }

    initKngSubmittedApplications(apps: Application[]) {
        this._context.kngSubmittedApplications().init(apps)
        this._context.kngSubmittedApplications().currentApplication.next(
            apps.length > 0 ? ModifyApplicationFactory.from(apps[0]) : null
        )
    }

    getCurrentSubmittedKngApplication(): Observable<ModifyApplication | null> | undefined {
        return this._context.kngSubmittedApplications().currentApplication.asObservable()
    }

    setCurrentSubmittedKngApplicationField(key: string, value: any) {
        this._context.kngSubmittedApplications().setFieldCurrentApplication(key, value)
    }

    getCurrentSubmittedKngApplicationsIndex(): Observable<number> {
        return this._context.kngSubmittedApplications().currentIndex.asObservable()
    }

    setCurrentSubmittedKngApplication(index: number) {
        this._context.kngSubmittedApplications().setCurrentApplication(index)
    }

    setSuperior(createSuperiorMap: CreateSuperiorMap) {
        return this._kngService.SetSuperior(createSuperiorMap)
    }

    getSuperiorMaps(): Observable<SuperiorMap[]> {
        return this._kngService.GetSuperiorMaps().pipe(first())
    }

    nextKngApplicationState() {
        this._context.kngSubmittedApplications().nextApplication()
    }

    cancelKngApplication(applicationId: string) {
        this._kngService
            .Cancel(applicationId)
            .pipe(first())
            .subscribe({
                next: res => {
                    this.setKng(
                        this._context.kng().subjects.items.value.map(k => (k.masterId === res.masterId ? res : k))
                    )
                    this._context.kng()
                        .sendResult(ResultFactory.createSuccess("Uspješno otkazano"))
                    this.setCurrentEditKng(undefined)
                },
                error: error => {
                    if (typeof error.error === "string") {
                        this._context.kng().sendResult(ResultFactory.createError(error.error, []))
                    } else if (typeof error.error === "object" && error.error.message) {
                        this._context.kng().sendResult(
                            ResultFactory.createError(error.error.message, error.error.incorrectFields || [])
                        )
                    } else {
                        this._context.kng().sendResult(ResultFactory.createError("An error occurred", []))
                    }
                },
            })
    }

    generateContracts(applicationId: string, body: ContractGenerate): Observable<Application> {
        return this._kngService.GenerateContracts(applicationId, body).pipe(
            tap(res => {
                this.setKng(
                    this._context.kng().subjects.items.value.map(a => a.masterId === applicationId ? res : a)
                )
                this._context.kng().sendResult(ResultFactory.createSuccess("Ugovori kreirani"))
            }),
            catchError(err => {
                let msg = "An error occurred"

                if (typeof err.error === "string") {
                    msg = err.error
                } else if (typeof err.error === "object" && err.error.message) {
                    msg = err.error.message
                }

                this._context.kng().sendResult(ResultFactory.createError(msg, []))
                throw err
            })
        )
    }

    adminModifyContracts(applicationId: string, contracts: { rentalAgreement: FileContent, subrentalAgreement: FileContent }): Observable<Application> {
        return this._kngService.AdminModifyContracts(applicationId, contracts).pipe(
            tap(res => {
                this.setKng(
                    this._context.kng().subjects.items.value.map(a => a.masterId === applicationId ? res : a)
                )
                this._context.kng().sendResult(ResultFactory.createSuccess("Ugovori spremljeni i status je postavljen na subvenciju u tijeku"))
            }),
            catchError(err => {
                let msg = "An error occurred"

                if (typeof err.error === "string") {
                    msg = err.error
                } else if (typeof err.error === "object" && err.error.message) {
                    msg = err.error.message
                }

                this._context.kng().sendResult(ResultFactory.createError(msg, []))
                throw err
            })
        )
    }

    confirmSignedContracts(applicationId: string): Observable<Application> {
        return this._kngService.ConfirmSignedContracts(applicationId).pipe(
            tap(res => {
                this.setKng(
                    this._context.kng().subjects.items.value.map(a => a.masterId === applicationId ? res : a)
                )
                this._context.kng().sendResult(ResultFactory.createSuccess("Status je postavljen na subvenciju u tijeku"))
            }),
            catchError(err => {
                let msg = "An error occurred"

                if (typeof err.error === "string") {
                    msg = err.error
                } else if (typeof err.error === "object" && err.error.message) {
                    msg = err.error.message
                }

                this._context.kng().sendResult(ResultFactory.createError(msg, []))
                throw err
            })
        )
    }

    approveSubmittedKngApplication(): Observable<void> {
        const currValue = this._context.kngSubmittedApplications().currentApplication.value
        if (!currValue) return of()
        const oldValue = this._context.kngSubmittedApplications().submittedApplications.value.find(
            app => app.masterId === currValue.masterId
        )
        if (!oldValue) return of()

        const patchDocument = this._jsonPatchDocumentGenerator.getJsonPatchDocument(
            ModifyApplicationFactory.from(oldValue),
            currValue,
            this._ignoreFields
        )

        return this._kngService.ModifyForEvaluation(
            ModifyObjectFactory.create(patchDocument, oldValue.version),
            currValue.masterId
        ).pipe(
            switchMap(res => {
                this.setKng(
                    this._context.kng().subjects.items.value.map(a => a.masterId === currValue.masterId ? res : a)
                )
                this._context.kngSubmittedApplications().removeApplication()
                this._context
                    .kngSubmittedApplications()
                    .sendResult(ResultFactory.createSuccess("Spremno na procjenu nadređenog"))

                return of()
            }),
            catchError(err => {
                let msg = "An error occurred"
                let incorrectFields = []

                if (typeof err.error === "string") {
                    msg = err.error
                } else if (typeof err.error === "object" && err.error.message) {
                    msg = err.error.message
                    incorrectFields = err.error.incorrectFields || []
                }
                this._context.kngSubmittedApplications().sendResult(
                    ResultFactory.createError(msg, incorrectFields)
                )
                throw err
            }),
        )
    }

    getPositionScores(): Observable<PositionScore[]> {
        return this._context.kngPositionScores().positionScores.asObservable()
    }

    getUnassignedPositions(): Observable<Position[]> {
        return this._context.kngPositionScores().unassignedPositions.asObservable()
    }

    addPositionScore(createPositionScore: CreatePositionScore) {
        return this._kngService
            .SetPositionScore(createPositionScore)
            .pipe(tap(res => this.addPositionScoreToContext(res)), first())
    }

    updatePositionScore(updatedPositionScore: PositionScore) {
        const newValue = JSON.parse(JSON.stringify(updatedPositionScore))
        const oldValue = JSON.parse(
            JSON.stringify(
                this._context
                    .kngPositionScores()
                    .positionScores.value.find(ps => ps.positionId === updatedPositionScore.positionId)
            )
        )
        const patchDocument = this._jsonPatchDocumentGenerator.getJsonPatchDocument(
            oldValue,
            newValue,
            [...this._ignoreFields, 'positionId']
        )

        const modifyObject = ModifyObjectFactory.create(patchDocument, oldValue.version)
        return this._kngService
            .ModifyPositionScore(oldValue.masterId, modifyObject)
            .pipe(tap(res => this.updatePositionScoreInContext(res)), first())
    }

    addPositionScoreToContext(positionScore: PositionScore) {
        this._context.kngPositionScores().addPositionScore(positionScore);
    }

    updatePositionScoreInContext(positionScore: PositionScore) {
        this._context.kngPositionScores().updatePositionScore(positionScore);
    }

    ApproveApplication(applicationId: string): void {
        this._kngService.Approve(applicationId).subscribe({
            next: res => {
                this.setKng(
                    this._context.kng().subjects.items.value.map(a => a.masterId === applicationId ? res : a)
                )
                this._context.kng().sendResult(
                    ResultFactory.createSuccess("Uspješno odobreno")
                )
            },
            error: err => {
                this._context.kng().sendResult(
                    ResultFactory.createError(`Greška: ${err.error.message || "Greška pri odobrenju"}`, [])
                )
            }
        })
    }

    RejectApplication(applicationId: string): void {
        this._kngService.Reject(applicationId).subscribe({
            next: res => {
                this.setKng(
                    this._context.kng().subjects.items.value.map(a => a.masterId === applicationId ? res : a)
                )
                this._context.kng().sendResult(
                    ResultFactory.createSuccess("Uspješno odbijeno")
                )
            },
            error: err => {
                this._context.kng().sendResult(
                    ResultFactory.createError(`Greška: ${err.error.message || "Greška pri odbijanju"}`, [])
                )
            }
        })
    }
}
